import { WizEventManager, WizIcon, WizPopUp, WizRoot, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { FreedataUpdatePack, LoopResult, LoopVO } from '@wizeoo/akm-compute-lib';
import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import {
  Baseline,
  DataPack,
  EntityModificationTrace,
  SyncError
} from '@wizeoo/akm-message-lib';
import { CompositeSourcePack, UpdateEntry } from '../../command/core/sync/sync.service';
import { DataCommitCmd } from '../../command/em/datacommit.cmd';
import { AlkEvent } from '../../framework/events/alk.event';
import { ALK_EVENT_TYPE } from '../../model/enums';
import { Model } from '../../model/model';
import { ViewData } from '../../model/view.data';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { DataCheckCmd } from '../../command/em/datacheck.cmd';

/**
 * create update delete sur des éléments composites (FDV & Baseline) avec éventuellement enfants
 */
export class CompositeEngine {

  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }

  /* ################################################
      enregistrement des modifications composites
     ################################################ */

  /** 
   * enregistrement composite des modifications: FDV / BL / BLV en one shot
   * 
   * @param source CompositeSourcePack contient l'ensemble des modifs FD/ FDV / BL / BLV
   * @param callback pour retour éventuel en vue (appel de vue Freedata)
   */
  public compositeCommit(source: CompositeSourcePack, callback?: Function, forceSilent?: boolean): void {

    let silent: boolean = false;
    if(forceSilent) { // forçage mode silencieux depuis FDGrid
      silent = true;
    } else {
      if(!source.freedata && source.updates?.length <= 50) {
        silent = true;
      }
    }


    new DataCommitCmd(this.model).commit(source, this.backFromDataCommit.bind(this), callback)
      .silent(silent)
      .execute();
  }

  /** 
   * chargement des modifications: FDV / BL / BLV en one shot sur modif externe (cross / batch / other player)
   * 
   * @param source CompositeSourcePack contient l'ensemble des modifs FD/ FDV / BL / BLV
   */
  public compositeCheck(): void {
    new DataCheckCmd(this.model).check(this.backFromDataCommit.bind(this))
    .silent(true)  
    .execute();
  }

  /**
   * retour de service compositeCommit
   * va déclencher la synchro du cache local
   * la mise à jour des valeurs de la matrice
   * puis les recalculs nécessaires
   * @param cmd 
   */
  public backFromDataCommit(cmd: DataCommitCmd | DataCheckCmd): void {

    // console.log('backFromDataCommit');
    // ViewData.doEnd('compositeCommit');

    let csp: CompositeSourcePack;
    if(cmd instanceof DataCommitCmd) {
      csp = cmd.sourceComposite; // les packs transmis au service
      ViewData.wizUpdateModel = false; // modèle OK (rollback en cas d'erreur)
    }
    
    const commitResult: DataPack = cmd.result; // le retour de service

    // try {
    //   console.log('backFromDataCommit', commitResult.datas['3']['MAIN'].baselineValues[0].valor);
    // } catch(e: any) {

    // }



    /* ################################################
          gestion des erreurs
     ################################################ */

    let fatalError: boolean = false; // oups, raté...

    if (cmd.state === WIZ_COMMAND_STATE.FAULT) { // erreur physique
      fatalError = true;
    }

    const errors: SyncError[] = cmd.result?.errors; // erreurs métier
    if (!fatalError && !!errors) {
      errors.forEach((be: SyncError) => {
        switch (be.level) {
          case 'GLOBAL':
            this.niceErrorMessage(be.message, be.emt);
            fatalError = true;
            break;
          case 'WARN':
            // blindage : pas de warning self
            if (be.emt?.act === this.model.connectedUser.mail) {
              // faux positif, blinder dataCommit
              console.log('syncError self user', be.message, be.emt);
            } else {
              this.niceErrorMessage(be.message, be.emt);
            }
            break;
        }
      });
    }

    const packStatus: string = cmd.result?.status; // le statut du pack
    if (!fatalError && packStatus === WIZ_COMMAND_STATE.FAULT) { // erreur de service
      fatalError = true;
    }

    if (fatalError) { // erreur métier bloquante
      if (!!csp?.updates) {

        let loopResults: Map<FeiVO, LoopResult> = new Map(); // on va préparer un loop résult pour refresh visuellement les rollBacks

        csp.updates.forEach((update: UpdateEntry) => {
          this.model.dataEngine.rollBackUpdate(update, loopResults);
        });

        this.model.dataEngine.finalizeCompute(loopResults, null);
      }

      return; // fin du traitement
    }

    // pack de modifs sur les Freedatas et FreedataValues
    let freedataUpdates: Map<string, FreedataUpdatePack> = null;

    /* ################################################
          gestion Freedata
     ################################################ */

    if (!!commitResult.inFreedata || 
      commitResult.remoteFreedata?.length > 0 ||
      commitResult.remoteFreedataValues?.length > 0 ||
      commitResult.deletedFreedataAndValues?.length > 0 
    ) {

      // mise à jour du cache local
      this.model.freedataSyncService.eatRemote(commitResult);
      // merge des collections en mémoire
      freedataUpdates = this.model.freedataEngine.mergeFromCompositeCommit(commitResult);
    }
    
    if(cmd instanceof DataCommitCmd) {
          if (!!cmd.viewCallback) { // en cas d'appel depuis la vue Freedata
          cmd.viewCallback(csp);
        }
    }


    /* ################################################
          gestion Baselines et values
     ################################################ */

    // pack de modifs sur les filterKeys
    // les modifications identifiées sur les filterKeys Map<Baseline> = Set<codes filter modifiées>
    let filterKeyUpdates: Map<Baseline, Set<string>> = null;

    // on synchronise le cache local pour les BL / BLVs. C'est la qu'on intercepte les modifications sur les filterKeys
    filterKeyUpdates = this.model.widgetDataService.eatRemote(commitResult);
    // console.log('filterKey upd', filterKeyUpdates);

    /* ################################################
          Mémorisation du pack dans le model pour suivi & éviter double synchro
     ################################################ */

    /* ################################################
          Mise à jour de la matrice de calculs
     ################################################ */

    // on alimente les jeux locaux et on recalcul
    let loopvo: LoopVO = { loopResults: new Map(), pendings: new Map()};
    this.model.dataEngine.reComputeSteps(loopvo, commitResult, filterKeyUpdates, freedataUpdates);

    if (freedataUpdates?.size > 0) {// !!commitResult.inFreedata) {
      // console.log('compositeEngine FDU', freedataUpdates, commitResult);
      // et information pour les Widgets de type Freedata
      WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.FREEDATA_UPDATED, freedataUpdates));
    }

    let commitRevNumber: number = this.model.dataEngine.datapackMaxRevisionNumber(commitResult);
    if(commitRevNumber > -1) {
      this.model.commitMap.set(commitRevNumber, commitResult);
      // console.log('#### backFromCommit', commitRevNumber, fromSocket);
      this.model.dataEngine.fastSyncRemoteGrid(commitResult); // sync des grids remote (juste les BLVs)
    }
  }

  /** joli message d'erreur */
  public niceErrorMessage(kind: string, emt: EntityModificationTrace): void {
    let mess: string = WizTranslationManager.getInstance().translateKey(kind);
    mess = mess + '<br>';
    // mess += emt.actor + ' ' + emt.date; // TODO format correct pour heures (WizDateUtils.numberToStringDate(emt.date, WizDateUtils.FULL_FORMAT_MASK));
    if (!!emt.act) {
      mess += emt.act + ' ';
    }
    if (!!emt.ts) {
      mess += emt.ts;
    }
    WizPopUp.toast(mess, WizIcon.TOAST_WARNING, 5000, true, false).show();
  }


  /** forcer le rechargement du jeu local (FD / BL) */
  public compositeReload(args: any = null): void {
    // TODOTODO synchroniser uniquement le nécessaire pour les fei . BL
    ViewData.wizUpdateModel = false; // bourrin, faire plus élégant

    this.model.freedataSyncService.resetAll();
    this.model.cellMatrixEngine.clearFDV(); // rincer la matrice de cellules

    this.model.widgetDataService.resetAll();
    this.model.dataEngine.clearFei(); // suppression des FEI chargés
    this.model.cellMatrixEngine.clear(); // rincer la matrice de cellules

    this.model.workcomSyncService.resetAll();// reset sur les workcoms

    this.model.matrixSyncService.resetAll(); // reset sur les matrices


    // on charge les messages // TODO seulement quand nécessaire
    this.model.workcomSyncService.findWorkcoms(this.backFromWorkcom.bind(this), true);
    
  }

  private backFromWorkcom(cmd: any): void {
    //console.log('backFromWorkcom', cmd);
    // on charge les matrices
    this.model.matrixSyncService.find(this.backFromMatrix.bind(this), true); // vérif des FDs
  }


  private backFromMatrix(cmd: any): void {
    //console.log('backFromMatrix', cmd);
    // on charge les FDVs
    this.model.freedataSyncService.findFreedata(this.backFromReload.bind(this), true); // vérif des FDs
  }

  /** retour de clean, rechargement */
  private backFromReload(cmd: any): void {
    //console.log('backFromReload', cmd);
    WizEventManager.dispatchEvent(WizRoot.root(), new AlkEvent(ALK_EVENT_TYPE.WIDGET_MUST_REFRESH, null)); // pour les widgets
  }


}
